import { useMemo, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import { blogPosts } from '@/mocks/blogs';

// Weekly picks — cycles through this list based on the current week number
const weeklyPickSchedule = [
  { productId: 9,  blogId: 4,  reason: 'Cats go absolutely wild for these creamy lickable treats — perfect for bonding and picky eaters alike. Our readers can\'t stop raving about them.' },
  { productId: 5,  blogId: 5,  reason: 'Da Bird is the gold standard of feather wands. The way it mimics real bird flight triggers every hunting instinct your cat has.' },
  { productId: 19, blogId: 6,  reason: 'Shedding season is real. The FURminator cuts loose fur by up to 90% in one session — your couch will thank you.' },
  { productId: 27, blogId: 4,  reason: 'Purina Pro Plan\'s salmon & rice formula is a vet favourite for sensitive stomachs. Consistently top-rated by cat nutritionists.' },
  { productId: 17, blogId: 8,  reason: 'The donut shape cradles anxious cats perfectly. If your cat has stress or separation anxiety, this bed is a game-changer.' },
  { productId: 29, blogId: 6,  reason: 'Hertzko\'s self-cleaning brush is the smoothest grooming experience we\'ve tested — gentle bristles, effortless fur release.' },
  { productId: 25, blogId: 4,  reason: 'Blue Buffalo Wilderness puts real chicken first with zero grains. A premium pick for cats who deserve the best nutrition.' },
  { productId: 1,  blogId: 5,  reason: 'The KONG Wobbler turns mealtime into playtime. It slows down fast eaters and keeps curious cats mentally sharp.' },
];

function getWeekNumber(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = date.getTime() - start.getTime();
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  return Math.floor(diff / oneWeek);
}

export default function PickOfTheWeek() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const pick = useMemo(() => {
    const weekNum = getWeekNumber(new Date());
    const schedule = weeklyPickSchedule[weekNum % weeklyPickSchedule.length];
    const product = products.find((p) => p.id === schedule.productId);
    const blog = blogPosts.find((b) => b.id === schedule.blogId);
    return { product, blog, reason: schedule.reason };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!pick.product || !pick.blog) return null;

  const { product, blog, reason } = pick;

  const getRetailerLabel = (retailer: string) => {
    if (retailer === 'amazon') return 'Amazon';
    if (retailer === 'chewy') return 'Chewy';
    if (retailer === 'petsmart') return 'PetSmart';
    return 'Shop Now';
  };

  const getRetailerIcon = (retailer: string) => {
    if (retailer === 'amazon') return 'ri-amazon-line';
    if (retailer === 'chewy') return 'ri-shopping-bag-3-line';
    return 'ri-store-3-line';
  };

  return (
    <section 
      ref={sectionRef}
      className={`px-4 py-8 md:py-14 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 md:mb-8">
          <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-gradient-to-br from-amber-400 to-amber-500 rounded-full shadow-lg flex-shrink-0 animate-pulse">
            <i className="ri-trophy-line text-white text-xl md:text-2xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 leading-tight">Pick of the Week 🐾</h2>
            <p className="text-xs md:text-sm text-gray-500 mt-0.5">Hand-selected by our cat-loving editors</p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-2.5 py-1.5 md:px-4 md:py-2 rounded-full text-xs font-semibold flex-shrink-0 shadow-sm">
            <i className="ri-calendar-check-line text-sm md:text-base"></i>
            <span>Weekly</span>
          </div>
        </div>

        {/* Card - Mobile Stacked Layout */}
        <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-amber-100">
          {/* Decorative ribbon with badge overlay */}
          <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-black px-4 py-2 md:px-5 md:py-2.5 rounded-br-2xl tracking-widest uppercase shadow-lg z-10 flex items-center gap-1.5">
            <i className="ri-star-fill text-sm"></i>
            <span>This Week</span>
          </div>

          <div className="flex flex-col lg:flex-row">
            {/* Product Image - Full Width on Mobile, 4:3 Aspect Ratio */}
            <div className="relative overflow-hidden lg:w-2/5 bg-white">
              <div className="w-full aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[450px]">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-center"
                />
                {/* Badge overlay on image */}
                {product.badge && (
                  <div className={`absolute top-4 right-4 text-xs font-black px-3 py-1.5 rounded-full shadow-lg uppercase tracking-wide ${
                    product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                    product.badge === 'BESTSELLER' ? 'bg-amber-500 text-white' :
                    'bg-teal-500 text-white'
                  }`}>
                    {product.badge}
                  </div>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-amber-50/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-amber-50/60"></div>
            </div>

            {/* Content - Stacked Below on Mobile */}
            <div className="flex-1 p-5 md:p-8 lg:p-10 flex flex-col justify-center">
              {/* Category badge */}
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide shadow-sm">
                  {product.category}
                </span>
              </div>

              {/* Product name */}
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-800 mb-3 md:mb-4 leading-tight">
                {product.name}
              </h3>

              {/* Honest badge replacing fake stars */}
              <div className="flex items-center gap-2 mb-4 md:mb-5">
                <i className="ri-medal-line text-amber-500 text-xl"></i>
                <span className="text-sm md:text-base font-semibold text-amber-600">Highly Rated on Chewy</span>
              </div>

              {/* Editor's reason - Pull Quote Style */}
              <div className="bg-white/80 backdrop-blur-sm border-l-4 border-amber-400 rounded-r-xl px-4 md:px-6 py-4 md:py-5 mb-5 md:mb-6 shadow-md">
                <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <i className="ri-quill-pen-line"></i> Editor's Note
                </p>
                <p className="text-gray-700 text-sm md:text-base leading-relaxed italic font-medium">
                  "{reason}"
                </p>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2 md:gap-3 mb-6 md:mb-7">
                <span className="text-3xl md:text-4xl font-black text-teal-600">${product.price}</span>
                {product.originalPrice && (
                  <>
                    <span className="text-lg md:text-xl text-gray-400 line-through font-semibold">${product.originalPrice}</span>
                    <span className="bg-rose-100 text-rose-600 text-xs md:text-sm font-bold px-2.5 py-1 rounded-full shadow-sm">
                      Save ${(product.originalPrice - product.price).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {/* CTA Buttons - Stacked Full Width on Mobile */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5 md:mb-6">
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 active:from-teal-600 active:to-teal-700 text-white font-bold text-base md:text-lg py-4 md:py-3.5 px-6 md:px-8 rounded-full transition-all shadow-lg shadow-teal-500/30 active:shadow-xl active:shadow-teal-500/50 active:scale-[0.98] whitespace-nowrap cursor-pointer"
                >
                  <i className={`${getRetailerIcon(product.retailer)} text-xl`}></i>
                  Shop on {getRetailerLabel(product.retailer)}
                </a>
                <Link
                  to="/blog"
                  className="flex items-center justify-center gap-2 bg-white active:bg-amber-50 text-amber-700 font-bold text-base md:text-lg py-4 md:py-3.5 px-6 rounded-full border-2 border-amber-300 active:border-amber-400 transition-all shadow-md active:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-article-line text-xl"></i>
                  Why We Chose It →
                </Link>
              </div>

              {/* Linked blog teaser - Compact and Touch-Friendly */}
              <div className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-amber-200 shadow-sm active:shadow-md transition-shadow cursor-pointer">
                <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 rounded-lg md:rounded-xl overflow-hidden shadow-md">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-amber-600 font-bold mb-1 flex items-center gap-1 uppercase tracking-wide">
                    <i className="ri-book-open-line"></i>
                    Related Read
                  </p>
                  <Link
                    to="/blog"
                    className="text-sm md:text-base font-bold text-gray-800 active:text-teal-600 transition-colors line-clamp-2 cursor-pointer leading-snug"
                  >
                    {blog.title}
                  </Link>
                </div>
                <Link
                  to="/blog"
                  className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-amber-100 active:bg-amber-200 rounded-full text-amber-600 text-lg transition-all shadow-sm active:shadow-md flex-shrink-0 cursor-pointer"
                >
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}